/**
 * 個別処理モード専用プレビューパネル
 *
 * 機能:
 * - Before/After切り替えボタン
 * - コントラスト・明るさ・ホワイトバランス調整のリアルタイム反映
 * - AI加工履歴画像の表示
 * - デバウンス処理によるパフォーマンス最適化
 */

import { ThunkDispatch } from '@reduxjs/toolkit';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';

import { imageEditingActions, ImageEditingState, ProcessingHistory, UploadedImage } from '../../reducers/imageEditing';
import { applyImageAdjustments, extractAdjustmentOptions } from '../../utils/imageAdjustmentPipeline';
import { debounce } from '../../utils/debounce';
import CanvasImageDisplay from './shared/CanvasImageDisplay';
import { AIProcessingPopup } from '../common/AIProcessingPopup';

// State型定義
interface RootState {
  imageEditing: ImageEditingState;
  [key: string]: any;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  return {
    selectedImage: state.imageEditing.selectedImage,
    editHistory: state.imageEditing.editHistory,
    currentHistoryIndex: state.imageEditing.currentHistoryIndex,
    showAfter: state.imageEditing.showAfter,
    features: state.imageEditing.features,
    isAIProcessing: state.imageEditing.isAIProcessing,
    uploadedImages: state.imageEditing.uploadedImages,
  };
}

interface IndividualPreviewPanelProps {
  selectedImage: UploadedImage | null;
  editHistory: { [imageId: string]: ProcessingHistory[] };
  currentHistoryIndex: { [imageId: string]: number };
  showAfter: { [imageId: string]: boolean };
  features: ImageEditingState['features'];
  isAIProcessing: boolean;
  uploadedImages: UploadedImage[];
}

const IndividualPreviewPanel: React.FC<IndividualPreviewPanelProps> = (props) => {
  const {
    selectedImage,
    editHistory,
    currentHistoryIndex,
    showAfter,
    features,
    isAIProcessing,
    uploadedImages,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [displayImageBlob, setDisplayImageBlob] = useState<Blob | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previousAdjustments, setPreviousAdjustments] = useState<string>('');

  // displayImageBlobの変化をログ
  useEffect(() => {
    console.log('[IndividualPreviewPanel] displayImageBlob changed:', displayImageBlob ? displayImageBlob.size : 'null');
  }, [displayImageBlob]);

  // 表示すべき画像のBlobを取得
  const getDisplayImageBlob = useCallback(async (): Promise<Blob | null> => {
    if (!selectedImage) return null;

    try {
      // Before表示時: 元画像（調整なし）
      const isAfterMode = showAfter[selectedImage.id] ?? false;
      if (!isAfterMode) {
        return selectedImage.file;
      }

      // After表示時: 履歴画像 or 元画像
      const history = editHistory[selectedImage.id] || [];
      const currentIndex = currentHistoryIndex[selectedImage.id] ?? -1;

      let blob: Blob;

      // currentIndex === 0 は履歴パネルで「元画像」が選択された状態
      if (history.length > 0 && currentIndex !== 0) {
        // 履歴選択状態: fetchを使用して画像を取得
        // 注意: 履歴パネルでは元画像がindex 0なので、実際の履歴はindex - 1
        const targetIndex = currentIndex === -1 ? history.length - 1 : currentIndex - 1;
        const { processedImageUrl } = history[targetIndex];
        console.log('[IndividualPreviewPanel] Fetching from history:', processedImageUrl, 'targetIndex:', targetIndex);
        const response = await fetch(processedImageUrl);
        blob = await response.blob();
        console.log('[IndividualPreviewPanel] Fetched blob from history:', blob.size);
      } else {
        // 元画像を使用（currentIndex === 0 または履歴なし）
        console.log('[IndividualPreviewPanel] Using original file, currentIndex:', currentIndex);
        // selectedImage.fileを直接Blobとして扱うのではなく、
        // 新しいBlobオブジェクトとして作成し直す
        blob = new Blob([await selectedImage.file.arrayBuffer()], { type: selectedImage.file.type });
        console.log('[IndividualPreviewPanel] Created new blob from file:', blob.size, blob.type);
      }

      // After表示時のみ調整を適用
      const adjustmentOptions = extractAdjustmentOptions(features);
      console.log('[IndividualPreviewPanel] Adjustment options:', adjustmentOptions);
      console.log('[IndividualPreviewPanel] Features:', features);

      // 調整が何もない場合は早期リターン（ウォーターマークも含める）
      const hasAnyAdjustment =
        adjustmentOptions.contrast !== 0 ||
        adjustmentOptions.brightness !== 0 ||
        adjustmentOptions.saturation !== 0 ||
        (adjustmentOptions.whiteBalanceTemperature && adjustmentOptions.whiteBalanceTemperature !== 0) ||
        adjustmentOptions.watermark !== undefined;

      console.log('[IndividualPreviewPanel] Has any adjustment:', hasAnyAdjustment);

      if (hasAnyAdjustment) {
        console.log('[IndividualPreviewPanel] Applying adjustments...');
        blob = await applyImageAdjustments(blob, adjustmentOptions);
        console.log('[IndividualPreviewPanel] Adjustments applied');
      }

      return blob;
    } catch (error) {
      console.error('Failed to get display image blob:', error);
      return null;
    }
  }, [selectedImage, showAfter, editHistory, currentHistoryIndex, features]);

  // デバウンス付き画像更新処理（300ms）
  const debouncedUpdateImage = useMemo(
    () =>
      debounce(async () => {
        setIsProcessing(true);
        const blob = await getDisplayImageBlob();
        console.log('[IndividualPreviewPanel] Setting displayImageBlob:', blob ? blob.size : 'null');
        setDisplayImageBlob(blob);
        setIsProcessing(false);
      }, 300),
    [getDisplayImageBlob]
  );

  // 画像または調整が変更されたら再描画
  useEffect(() => {
    debouncedUpdateImage();

    // クリーンアップ
    return () => {
      debouncedUpdateImage.cancel();
    };
  }, [debouncedUpdateImage]);

  // 調整が変更された時、Beforeモードなら自動的にAfterモードに切り替え
  useEffect(() => {
    if (!selectedImage) return;

    const adjustmentOptions = extractAdjustmentOptions(features);
    const currentAdjustments = JSON.stringify(adjustmentOptions);

    // 調整値が実際に変更された場合のみ処理
    if (currentAdjustments !== previousAdjustments) {
      setPreviousAdjustments(currentAdjustments);

      // 初回レンダリング時はスキップ（previousAdjustmentsが空文字列の場合）
      if (previousAdjustments === '') {
        return;
      }

      const hasAnyAdjustment =
        adjustmentOptions.contrast !== 0 ||
        adjustmentOptions.brightness !== 0 ||
        adjustmentOptions.saturation !== 0 ||
        (adjustmentOptions.whiteBalanceTemperature && adjustmentOptions.whiteBalanceTemperature !== 0) ||
        adjustmentOptions.watermark !== undefined;

      const isAfterMode = showAfter[selectedImage.id];

      // Beforeモードで調整がある場合のみAfterモードに切り替え
      if (!isAfterMode && hasAnyAdjustment) {
        console.log('[IndividualPreviewPanel] Auto-switching to After mode due to adjustment change');
        dispatch(imageEditingActions.toggleShowAfter(selectedImage.id));
      }
    }
  }, [selectedImage, features, showAfter, dispatch, previousAdjustments]);

  // Before/After切り替えハンドラ
  const handleToggleShowAfter = () => {
    if (!selectedImage || isAIProcessing) return;
    dispatch(imageEditingActions.toggleShowAfter(selectedImage.id));
  };

  // 画像が選択されていない場合
  if (!selectedImage) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <svg
            className="w-16 h-16 mx-auto mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm">画像が選択されていません</p>
        </div>
      </div>
    );
  }

  const isAfterMode = showAfter[selectedImage.id] ?? false;
  console.log('[IndividualPreviewPanel] Button render - isAfterMode:', isAfterMode, 'type:', typeof isAfterMode, 'showAfter[id]:', showAfter[selectedImage.id]);

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Canvas画像表示 - 全領域背景 */}
      <div className="absolute inset-0">
        {displayImageBlob && !isProcessing ? (
          <CanvasImageDisplay
            imageSource={displayImageBlob}
            showDownloadButton
            maxWidth={1200}
            maxHeight={800}
            uploadedImages={uploadedImages}
            originalId={selectedImage.id}
            features={features}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-600">画像を処理中...</p>
            </div>
          </div>
        )}
      </div>

      {/* Before/After切り替えボタン - 右上に独立配置 */}
      <div className="absolute top-0 right-0 p-4 z-50">
        <button
          onClick={handleToggleShowAfter}
          disabled={isAIProcessing}
          style={{
            padding: '10px 20px',
            borderRadius: '9999px',
            fontWeight: 'bold',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s',
            backgroundColor: '#ffffff',
            color: '#000000',
            border: '2px solid #d1d5db',
            cursor: isAIProcessing ? 'not-allowed' : 'pointer',
            opacity: isAIProcessing ? 0.5 : 1,
          }}
          type="button"
        >
          {isAfterMode ? 'After' : 'Before'}
        </button>
      </div>

      {/* AI処理中ポップアップ */}
      <AIProcessingPopup
        show={isAIProcessing}
        title="AI加工を実行中..."
        description="AIが選択された加工処理を実行しています。"
        subDescription="しばらくお待ちください..."
      />
    </div>
  );
};

export default connect(mapStateToProps)(IndividualPreviewPanel);
